import { useState } from "react";
import type { ApiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import { FolderSelect, useFolders } from "./FolderSelect";

/**
 * File a contract into a folder from the contract's own page.
 *
 * Moving a contract needs edit rights on both ends, so the API can refuse this
 * with a 403 the person did not expect while looking at a contract they can
 * plainly see. The message is shown rather than swallowed.
 */
export function FolderPicker({
  api,
  contractId,
  folderId,
  onChanged,
}: {
  api: ApiClient;
  contractId: string;
  folderId: string | undefined;
  onChanged: (folderId: string | undefined) => void;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const { folders, error: loadError } = useFolders(api);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const change = async (next: string) => {
    const target = next === "" ? null : next;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.setContractFolder(contractId, target);
      onChanged(updated.folderId);
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      setError(describeError(caught, t));
    } finally {
      setSaving(false);
    }
  };

  const shown = error ?? loadError;

  return (
    <div className="ds-cols --middle --gap-small">
      <FolderSelect
        folders={folders}
        value={folderId ?? ""}
        disabled={saving}
        label={t("contracts.detail.folderSection")}
        onChange={(next) => void change(next)}
      />
      {shown !== null && (
        <span className="ds-text --small --alert">{shown}</span>
      )}
    </div>
  );
}
